import React, { useMemo, useState } from "react";
import { Box, Stack } from "@mui/material";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import GroupAddRoundedIcon from "@mui/icons-material/GroupAddRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import ChatBubbleRoundedIcon from "@mui/icons-material/ChatBubbleRounded";

import Dialog from "../../../components/dialog/Dialog";
import { GetUserGroupsByOrganisationId } from "../../../hooks/useUserGroupdata";
import { CreateGroupModal } from "../../../modals/general/CreateGroupModal";
import { GroupEditModal } from "../customer/GroupEditView";
import {
  Avatar,
  ClickableRow,
  CountDivider,
  EmptyState,
  ListShell,
  PrimaryButton,
  RowChevron,
  RowName,
  RowSkeleton,
  SearchBar,
  ToolbarRow,
} from "./orgTabsKit";

const PermissionDot = ({ icon: Icon, label, active }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      px: 1.5,
      height: 28,
      borderRadius: "999px",
      border: "1px solid",
      borderColor: active ? "rgba(43,140,77,0.32)" : "rgba(10,22,40,0.12)",
      background: active ? "rgba(43,140,77,0.1)" : "rgba(10,22,40,0.025)",
      color: active ? "#1a6536" : "rgba(95,104,119,0.75)",
      fontSize: "12px",
      fontWeight: 700,
      fontFamily: '"DM Sans", sans-serif',
    }}
  >
    <Icon sx={{ fontSize: 14 }} />
    {label}
  </Box>
);

export const OrgGroupsTab = ({ organisationId, orgName }) => {
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editGroupId, setEditGroupId] = useState(null);

  const { data, isLoading } = GetUserGroupsByOrganisationId(organisationId);

  const groups = useMemo(
    () => (Array.isArray(data?.data) ? data.data : []),
    [data]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) =>
      (g.group_name || "").toLowerCase().includes(q)
    );
  }, [groups, search]);

  return (
    <Box>
      <ToolbarRow>
        <SearchBar
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Sök grupp..."
        />
        <PrimaryButton
          icon={GroupAddRoundedIcon}
          label="Skapa grupp"
          onClick={() => setCreateOpen(true)}
        />
      </ToolbarRow>

      <CountDivider
        label={
          isLoading
            ? "Läser in grupper"
            : `${filtered.length} ${
                filtered.length === 1 ? "grupp" : "grupper"
              }`
        }
      />

      {isLoading ? (
        <RowSkeleton />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={GroupsRoundedIcon}
          title={search.trim() ? "Inga träffar" : "Inga grupper ännu"}
          hint={
            search.trim()
              ? "Prova en annan sökterm."
              : "Skapa en grupp för att börja organisera organisationens användare."
          }
        />
      ) : (
        <ListShell>
          {filtered.map((group) => (
            <ClickableRow
              key={group.id}
              columns="auto 1fr auto auto"
              onClick={() => setEditGroupId(group.id)}
            >
              <Avatar name={group.group_name || "?"} />
              <Box sx={{ minWidth: 0 }}>
                <RowName>{group.group_name || "Namnlös grupp"}</RowName>
              </Box>
              <Stack
                direction="row"
                spacing={0.75}
                sx={{ display: { xs: "none", sm: "flex" } }}
              >
                <PermissionDot
                  icon={NotificationsActiveRoundedIcon}
                  label="Larm"
                  active={!!group.can_alarm}
                />
                <PermissionDot
                  icon={ChatBubbleRoundedIcon}
                  label="Chatt"
                  active={!!group.can_chat}
                />
              </Stack>
              <RowChevron />
            </ClickableRow>
          ))}
        </ListShell>
      )}

      <Dialog
        title={`Skapa grupp · ${orgName}`}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      >
        <CreateGroupModal
          customerid={organisationId}
          closeCreateModal={() => setCreateOpen(false)}
        />
      </Dialog>

      {editGroupId && (
        <Dialog
          title="Redigera grupp"
          open={!!editGroupId}
          onClose={() => setEditGroupId(null)}
        >
          <GroupEditModal
            groupId={editGroupId}
            onClose={() => setEditGroupId(null)}
          />
        </Dialog>
      )}
    </Box>
  );
};

export default OrgGroupsTab;
